import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  Users,
  Handshake,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Building2,
  Loader2,
  RefreshCw,
} from "lucide-react"
import { useAuth, type AdminAgencyInfo } from "@/contexts/AuthContext"

type SortField = "name" | "ownerEmail" | "memberCount" | "dailyDeals" | "monthlyDeals" | "totalDeals" | "agencyId"
type SortOrder = "asc" | "desc"

function SortIcon({ field, activeField, activeOrder }: { field: SortField; activeField: SortField; activeOrder: SortOrder }) {
  if (field !== activeField) return <ArrowUpDown className="w-3 h-3 text-white/15" />
  return activeOrder === "asc"
    ? <ArrowUp className="w-3 h-3 text-[#F37020]" />
    : <ArrowDown className="w-3 h-3 text-[#F37020]" />
}

export function AdminAgencies() {
  const { getAdminAgenciesPaginated } = useAuth()
  const [agencies, setAgencies] = React.useState<AdminAgencyInfo[]>([])
  const [loading, setLoading] = React.useState(true)
  const [refreshing, setRefreshing] = React.useState(false)
  const [total, setTotal] = React.useState(0)
  const [page, setPage] = React.useState(1)
  const [limit, setLimit] = React.useState(10)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState("")
  const [debouncedSearch, setDebouncedSearch] = React.useState("")
  const [sortBy, setSortBy] = React.useState<SortField>("dailyDeals")
  const [sortOrder, setSortOrder] = React.useState<SortOrder>("desc")

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1) // reset to page 1 on new search
    }, 350)
    return () => clearTimeout(timer)
  }, [search])

  const fetchAgencies = React.useCallback(async (silent?: boolean) => {
    if (!silent) setLoading(true)
    else setRefreshing(true)

    const res = await getAdminAgenciesPaginated({
      page,
      limit,
      search: debouncedSearch || undefined,
      sortBy,
      sortOrder,
    })

    if (res.success) {
      setAgencies(res.agencies || [])
      setTotal(res.total || 0)
      setTotalPages(res.totalPages || 1)
      if (res.page) setPage(res.page)
    }

    if (!silent) setLoading(false)
    else setRefreshing(false)
  }, [getAdminAgenciesPaginated, page, limit, debouncedSearch, sortBy, sortOrder])

  React.useEffect(() => {
    fetchAgencies()
  }, [fetchAgencies])

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortBy(field)
      setSortOrder("desc")
    }
    setPage(1)
  }

  const startIndex = (page - 1) * limit + 1
  const endIndex = Math.min(page * limit, total)

  const columns: { key: SortField; label: string; align?: "right" | "center"; width?: string }[] = [
    { key: "name", label: "Agency" },
    { key: "agencyId", label: "ID" },
    { key: "ownerEmail", label: "Owner Email" },
    { key: "memberCount", label: "Members", align: "right", width: "w-24" },
    { key: "dailyDeals", label: "Daily", align: "right", width: "w-20" },
    { key: "monthlyDeals", label: "Monthly", align: "right", width: "w-24" },
    { key: "totalDeals", label: "Total Deals", align: "right", width: "w-28" },
  ]

  return (
    <div className="space-y-4">
      {/* Header Row */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#F37020]/10 border border-[#F37020]/20 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-[#F37020]/80" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-white/90 font-admin">Agencies</h2>
            <p className="text-xs text-white/30 font-admin">{total} total agencies</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/20" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search agencies..."
              className="w-56 pl-8 pr-8 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/70 font-admin placeholder:text-white/15 outline-none focus:border-[#F37020]/40 transition-colors"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/25 hover:text-white/50"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Refresh */}
          <button
            type="button"
            onClick={() => fetchAgencies(true)}
            disabled={refreshing}
            className="w-8 h-8 rounded-lg bg-[#0C0C12] border border-[#2A2A45] inline-flex items-center justify-center text-white/40 hover:text-white/70 hover:border-[#2A2A3A] transition-colors disabled:opacity-50"
          >
            {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="bg-[#111117] border border-[#2A2A45] rounded-xl overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Table Header */}
            <thead>
              <tr className="border-b border-[#2A2A45]">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={[
                      "px-4 py-3 text-[10px] uppercase tracking-wider font-semibold font-admin cursor-pointer select-none transition-colors hover:bg-white/[0.02]",
                      col.align === "right" ? "text-right" : "text-left",
                      col.width || "",
                      sortBy === col.key ? "text-[#F37020]/80" : "text-white/30",
                    ].join(" ")}
                    onClick={() => handleSort(col.key)}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.label}
                      <SortIcon field={col.key} activeField={sortBy} activeOrder={sortOrder} />
                    </span>
                  </th>
                ))}
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-6 h-6 border-2 border-[#F37020]/30 border-t-[#F37020] rounded-full animate-spin" />
                      <p className="text-xs text-white/25 font-admin">Loading agencies...</p>
                    </div>
                  </td>
                </tr>
              ) : agencies.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="py-16 text-center">
                    <Building2 className="w-6 h-6 text-white/10 mx-auto" />
                    <p className="text-sm text-white/25 font-admin mt-2">
                      {debouncedSearch ? `No agencies match "${debouncedSearch}"` : "No agencies found"}
                    </p>
                  </td>
                </tr>
              ) : (
                <AnimatePresence mode="popLayout">
                  {agencies.map((agency, i) => (
                    <motion.tr
                      key={agency.agencyId}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.02 }}
                      className="border-b border-[#2A2A45]/50 hover:bg-white/[0.015] transition-colors group"
                    >
                      {/* Agency Name */}
                      <td className="px-4 py-3.5">
                        <p className="text-sm font-medium text-white/85 font-admin group-hover:text-white transition-colors">
                          {agency.name}
                        </p>
                      </td>

                      {/* Agency ID */}
                      <td className="px-4 py-3.5">
                        <span className="text-[11px] font-mono text-white/20 bg-white/[0.04] px-2 py-0.5 rounded">
                          {agency.agencyId}
                        </span>
                      </td>

                      {/* Owner Email */}
                      <td className="px-4 py-3.5">
                        <p className="text-xs text-white/40 font-admin">{agency.ownerEmail}</p>
                      </td>

                      {/* Members */}
                      <td className="px-4 py-3.5 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <Users className="w-3 h-3 text-white/20" />
                          <span className="text-sm font-semibold text-white/70 font-admin tabular-nums">
                            {agency.memberCount}
                          </span>
                        </div>
                      </td>

                      {/* Daily Deals */}
                      <td className="px-4 py-3.5 text-right">
                        <span className={[
                          "text-sm font-semibold font-admin tabular-nums",
                          agency.dailyDeals > 0 ? "text-sky-400/80" : "text-white/25",
                        ].join(" ")}>
                          {agency.dailyDeals}
                        </span>
                      </td>

                      {/* Monthly Deals */}
                      <td className="px-4 py-3.5 text-right">
                        <span className={[
                          "text-sm font-semibold font-admin tabular-nums",
                          agency.monthlyDeals > 0 ? "text-emerald-400/80" : "text-white/25",
                        ].join(" ")}>
                          {agency.monthlyDeals}
                        </span>
                      </td>

                      {/* Total Deals */}
                      <td className="px-4 py-3.5 text-right">
                        <span className={[
                          "text-sm font-semibold font-admin tabular-nums",
                          agency.totalDeals > 0 ? "text-[#F37020]/80" : "text-white/25",
                        ].join(" ")}>
                          {agency.totalDeals}
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between gap-4 px-4 py-3 border-t border-[#2A2A45] flex-wrap">
            {/* Left: showing X of Y */}
            <p className="text-[11px] text-white/25 font-admin">
              Showing <span className="text-white/50">{startIndex}–{endIndex}</span> of <span className="text-white/50">{total}</span>
            </p>

            {/* Center: page buttons */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={page <= 1}
                className="w-7 h-7 rounded-md bg-[#0C0C12] border border-[#2A2A45] inline-flex items-center justify-center text-white/30 hover:text-white/60 hover:border-[#2A2A3A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronsLeft className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="w-7 h-7 rounded-md bg-[#0C0C12] border border-[#2A2A45] inline-flex items-center justify-center text-white/30 hover:text-white/60 hover:border-[#2A2A3A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
              </button>

              {/* Page numbers */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce<(number | "...")[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] ?? 0) > 1) acc.push("...")
                  acc.push(p)
                  return acc
                }, [])
                .map((item, idx) =>
                  item === "..." ? (
                    <span key={`ellipsis-${idx}`} className="w-7 h-7 inline-flex items-center justify-center text-[11px] text-white/15 font-admin">
                      ...
                    </span>
                  ) : (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setPage(item as number)}
                      className={[
                        "w-7 h-7 rounded-md text-[11px] font-medium font-admin inline-flex items-center justify-center transition-colors",
                        page === item
                          ? "bg-[#F37020]/15 border border-[#F37020]/30 text-[#F37020]"
                          : "bg-[#0C0C12] border border-[#2A2A45] text-white/30 hover:text-white/60 hover:border-[#2A2A3A]",
                      ].join(" ")}
                    >
                      {item}
                    </button>
                  )
                )}

              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="w-7 h-7 rounded-md bg-[#0C0C12] border border-[#2A2A45] inline-flex items-center justify-center text-white/30 hover:text-white/60 hover:border-[#2A2A3A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
                className="w-7 h-7 rounded-md bg-[#0C0C12] border border-[#2A2A45] inline-flex items-center justify-center text-white/30 hover:text-white/60 hover:border-[#2A2A3A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronsRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Right: rows per page */}
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-white/25 font-admin">Rows</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value))
                  setPage(1)
                }}
                className="bg-[#0C0C12] border border-[#2A2A45] text-xs text-white/60 font-admin rounded-md px-2 py-1 outline-none focus:border-[#F37020]/40 cursor-pointer appearance-none"
              >
                {[10, 25, 50].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  )
}
